import { useState } from 'react';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { usePrompts } from '../../hooks/usePrompts';
import { useAssignPrompt } from '../../hooks/useAgents';
import { Prompt } from '../../types';

interface AgentPromptSelectorProps {
  agentId: string;
  isOpen: boolean;
  onClose: () => void;
}

export function AgentPromptSelector({ agentId, isOpen, onClose }: AgentPromptSelectorProps) {
  const [search, setSearch] = useState('');
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
  const { data, isLoading, error } = usePrompts(1, 100, { search: search || undefined });
  const assignPrompt = useAssignPrompt();

  const prompts = data?.data || [];

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !assignPrompt.isPending) {
      handleClose();
    }
  };

  const handleClose = () => {
    setSearch('');
    setSelectedPrompt(null);
    onClose();
  };

  const handleSelectPrompt = (prompt: Prompt) => {
    setSelectedPrompt(prompt);
  };

  const handleAssign = async () => {
    if (!selectedPrompt) return;

    try {
      await assignPrompt.mutateAsync({ agentId, promptId: selectedPrompt.id });
      handleClose();
    } catch (error) {
      console.error('Failed to assign prompt:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="prompt-selector-title"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 id="prompt-selector-title" className="text-lg font-medium text-gray-900 dark:text-white">
            Select Prompt
          </h3>
          <button
            type="button"
            onClick={handleClose}
            disabled={assignPrompt.isPending}
            className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Close modal"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-auto px-6 py-4">
          {/* Search Bar */}
          <div className="mb-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Search prompts..."
              />
            </div>
          </div>

          {/* Prompt List */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-sm text-gray-500 dark:text-gray-400">Loading prompts...</div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-sm text-red-600 dark:text-red-400">
                Error loading prompts. Please try again.
              </p>
            </div>
          ) : prompts.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {search ? `No prompts found matching "${search}"` : 'No prompts available'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {prompts.map((prompt) => (
                <button
                  key={prompt.id}
                  type="button"
                  onClick={() => handleSelectPrompt(prompt)}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                    selectedPrompt?.id === prompt.id
                      ? 'border-indigo-600 dark:border-indigo-400 bg-indigo-50 dark:bg-indigo-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="font-medium text-gray-900 dark:text-white mb-2">
                    {prompt.name}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                    {prompt.content}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 rounded-b-lg">
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={assignPrompt.isPending}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAssign}
              disabled={!selectedPrompt || assignPrompt.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 dark:bg-indigo-700 border border-transparent rounded-md hover:bg-indigo-700 dark:hover:bg-indigo-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {assignPrompt.isPending ? 'Assigning...' : 'Assign Prompt'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
